// kfe-html-pane.js — sandboxed HTML reader for HTML / AudioHTML items.
//
// PORT TARGET: ~/Developer/Rhythmic-Mandarin-Web-delivery_V1/src/ui/rm-html-pane.js
//
// The corpus (RefNote*, KFEDRABOUT, T3E*, WelcomeNewUniverse, RE0101D1, …)
// renders on iOS via `KFEWebView` — a thin WKWebView wrapper that calls
// `loadHTMLString:baseURL:`. The browser is already a WKWebView; we mount a
// same-origin iframe and let the browser do the rendering natively.
//
// Sandboxing posture: `sandbox="allow-same-origin"` (no `allow-scripts`).
// The corpus is static reading content — tables, paragraphs, the occasional
// `<img>`. Allowing scripts would widen the threat surface for no benefit.
// Re-evaluate if a future content type genuinely needs scripts.
//
// Sizing: iframes default to 150px tall, which crops every page in the
// corpus. After the iframe's `load` event we read body geometry and set
// `iframe.style.height` to match. ResizeObserver re-measures on inner
// layout changes; window resize listener handles outer viewport changes.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";

export class KFEHtmlPane extends KFEComponent {
  static props = ["htmlUrl"];

  // No class-field initializer for `htmlUrl`: KFEComponent installs a
  // reactive accessor in its constructor, and a class-field write here
  // would clobber that accessor with a plain data property (TC39
  // [[Define]] semantics). The constructor's accessor with `__htmlUrl`
  // undefined-by-default is what other KFE components rely on.

  /** @type {HTMLIFrameElement | null} */ _iframe = null;
  /** @type {number | null} */ _resizeRaf = null;
  /** @type {ResizeObserver | null} */ _innerObserver = null;
  /** @type {WeakSet<HTMLIFrameElement>} */ _wiredIframes = new WeakSet();

  static styles = `
    :host {
      display: block;
      max-width: 56rem;
      margin: 0 auto;
      background: var(--kfe-surface);
      color: var(--kfe-fg);
      border-radius: 0.5rem;
      overflow: hidden;
    }
    iframe {
      display: block;
      width: 100%;
      border: 0;
      background: #ffffff; /* corpus pages assume white background */
      color-scheme: light;
    }
  `;

  render() {
    const url = /** @type {any} */ (this).htmlUrl;
    if (!url) return html``;
    return html`
      <iframe
        sandbox="allow-same-origin"
        loading="lazy"
        referrerpolicy="no-referrer"
        src="${url}"
      ></iframe>
    `;
  }

  onConnected() {
    // _wireIframe is also called from _renderNow override below — wire here
    // too in case onConnected fires after an initial render that produced
    // a real iframe (e.g. on hot-reload where the prop was already set).
    this._wireIframe();
    const onResize = () => this._scheduleSize();
    window.addEventListener("resize", onResize);
    this.addCleanup(() => {
      window.removeEventListener("resize", onResize);
      if (this._resizeRaf !== null) cancelAnimationFrame(this._resizeRaf);
      this._innerObserver?.disconnect();
      this._innerObserver = null;
    });
  }

  // KFEComponent._renderNow() clears the shadow root on every render and
  // builds a fresh iframe element. Override so we (re)wire the iframe
  // immediately after each render — without this, htmlUrl arriving *after*
  // the initial empty render leaves the new iframe with no load listener
  // and the auto-size never fires.
  _renderNow() {
    super._renderNow();
    this._wireIframe();
  }

  _wireIframe() {
    const iframe = this.shadowRoot?.querySelector("iframe");
    if (!(iframe instanceof HTMLIFrameElement)) return;
    this._iframe = iframe;
    if (this._wiredIframes.has(iframe)) return; // already wired this DOM node
    this._wiredIframes.add(iframe);
    iframe.addEventListener("load", () => {
      // The corpus HTML files have no DOCTYPE, so the browser renders them
      // in quirks mode — and quirks-mode `<html>`/`<body>` height metrics
      // are unreliable. Inject a minimal CSS reset that forces the document
      // to take its natural content height, then measure.
      this._injectInnerStyles();
      this._sizeToContent();
      this._observeInner();
      // Belt-and-braces: re-measure after each <img> finishes loading. The
      // ResizeObserver should catch most cases, but image decode can
      // complete without triggering a body-size change depending on layout.
      const doc = iframe.contentDocument;
      if (doc) {
        for (const img of doc.images) {
          if (img.complete) continue;
          img.addEventListener("load", () => this._scheduleSize(), { once: true });
          img.addEventListener("error", () => this._scheduleSize(), { once: true });
        }
      }
      // Two safety re-measures in case ResizeObserver / img load both miss
      // a late reflow (e.g. Chinese-glyph fallback fonts swapping in).
      setTimeout(() => this._scheduleSize(), 250);
      setTimeout(() => this._scheduleSize(), 1500);
      // Brief 073: announce the loaded reading content + its word count so a
      // host (kfe-html-exercise) can derive the dwell-completion threshold.
      this._emitContentLoaded();
    });
  }

  /**
   * Emit `contentloaded` once the iframe document is available, carrying the
   * rendered word count. Same-origin only (the corpus is mounted same-origin);
   * if the body can't be read we still emit with a zero count so the host's
   * dwell timer can fall back to its floor.
   */
  _emitContentLoaded() {
    const doc = this._iframe?.contentDocument;
    const text = doc?.body?.innerText ?? "";
    const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
    this.emit("contentloaded", { wordCount });
  }

  /**
   * Observe layout changes inside the iframe so the host height tracks the
   * inner document. Fires whenever images load, fonts swap, anything that
   * changes body size — without that, the iframe stays at its under-
   * measured initial height and the page is cropped.
   */
  _observeInner() {
    const iframe = this._iframe;
    if (!iframe) return;
    const doc = iframe.contentDocument;
    if (!doc || !doc.body) return;
    this._innerObserver?.disconnect();
    if (typeof ResizeObserver !== "function") return;
    const ro = new ResizeObserver(() => this._scheduleSize());
    ro.observe(doc.body);
    ro.observe(doc.documentElement);
    this._innerObserver = ro;
  }

  _scheduleSize() {
    if (this._resizeRaf !== null) return;
    this._resizeRaf = requestAnimationFrame(() => {
      this._resizeRaf = null;
      this._sizeToContent();
    });
  }

  /**
   * Inject a minimal stylesheet inside the iframe so quirks-mode pages
   * report their full natural height regardless of any author CSS that
   * constrained `html`/`body` (legacy pages assume a parent with explicit
   * size and end up clamped inside an iframe). Same-origin only — we have
   * direct DOM access.
   */
  _injectInnerStyles() {
    const iframe = this._iframe;
    const doc = iframe?.contentDocument;
    if (!doc || !doc.head) return;
    if (doc.querySelector("style[data-kfe-pane]")) return;
    const style = doc.createElement("style");
    style.setAttribute("data-kfe-pane", "");
    style.textContent = `
      html, body {
        height: auto !important;
        min-height: 0 !important;
        max-height: none !important;
        overflow: visible !important;
        margin: 0;
      }
      body { padding: 1rem; box-sizing: border-box; }
      img { max-width: 100%; height: auto; }
    `;
    doc.head.appendChild(style);
  }

  _sizeToContent() {
    const iframe = this._iframe;
    if (!iframe) return;
    const doc = iframe.contentDocument;
    if (!doc || !doc.body) return; // cross-origin or not yet loaded
    // scrollHeight / offsetHeight are unreliable here: in quirks mode (no
    // doctype, which the corpus uses) browsers disagree on which property
    // is the "real" content height vs the viewport-clamped one, and once
    // the iframe is resized larger they tend to grow with the iframe in a
    // feedback loop.
    //
    // Walk the body's children and take the maximum `bottom` from
    // getBoundingClientRect — that's the actual layout position of the
    // last visible content, regardless of body / documentElement quirks.
    const body = doc.body;
    const bodyRect = body.getBoundingClientRect();
    let bottom = bodyRect.top; // floor in case body is empty
    for (const child of body.children) {
      const rect = child.getBoundingClientRect();
      if (rect.bottom > bottom) bottom = rect.bottom;
    }
    const measured = bottom - bodyRect.top;
    if (!Number.isFinite(measured) || measured <= 0) return;
    // Add the body's bottom padding (which child rects don't include).
    const cs = doc.defaultView?.getComputedStyle?.(body);
    const bottomPad = parseFloat(cs?.paddingBottom || "0") || 0;
    const h = Math.ceil(measured + bottomPad) + 4;
    // Avoid pointless writes that would re-trigger ResizeObserver. Allow a
    // 1-px tolerance so we don't oscillate on sub-pixel rounding.
    const current = parseFloat(iframe.style.height) || 0;
    if (Math.abs(current - h) < 2) return;
    iframe.style.height = `${h}px`;
  }
}
